import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Camera, Flag, Lightbulb, QrCode } from 'lucide-react'
import Button from './Button'
import Card from './Card'
import useTutorial from '../../hooks/useTutorial'
import { DEFAULT_TUTORIAL_CONTENT, TUTORIAL_CONTENT } from '../../lib/tutorialContent'

const HIGHLIGHT_CLASS = 'tutorial-highlight'

const INTRO_ICONS = { Flag, QrCode, Camera }

/** Estimated tip card height for placement (avoid measuring DOM). */
const SPOTLIGHT_CARD_ESTIMATE_PX = 320

function isIntroStep(step) {
  return step?.kind === 'intro' && Array.isArray(step?.blocks) && step.blocks.length > 0
}

function computeCardPlacement(cutout) {
  const margin = 12
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cardWidth = Math.min(576, vw - 2 * margin)
  const cx = cutout.left + cutout.width / 2
  const left = Math.max(margin, Math.min(cx - cardWidth / 2, vw - cardWidth - margin))
  const spaceBelow = vh - (cutout.top + cutout.height) - margin
  const spaceAbove = cutout.top - margin
  let top
  if (spaceBelow >= SPOTLIGHT_CARD_ESTIMATE_PX + margin) {
    top = cutout.top + cutout.height + margin
  } else if (spaceAbove >= SPOTLIGHT_CARD_ESTIMATE_PX + margin) {
    top = cutout.top - SPOTLIGHT_CARD_ESTIMATE_PX - margin
  } else {
    top = Math.max(margin, Math.min(vh - SPOTLIGHT_CARD_ESTIMATE_PX - margin, cutout.top + cutout.height + 8))
  }
  const maxHeight = Math.max(220, vh - top - margin)
  return { left, top, width: cardWidth, maxHeight }
}

/**
 * Dimmed regions around a viewport rectangle so the page stays visible in the hole.
 */
function SpotlightShroud({ rect, onDismissDim }) {
  const { top, left, width, height } = rect
  const stripClass = 'fixed z-[55] bg-black/70 pointer-events-auto'

  return (
    <>
      <div
        className={`${stripClass} left-0 right-0 top-0`}
        style={{ height: `${Math.max(0, top)}px` }}
        onClick={onDismissDim}
        aria-hidden
      />
      <div
        className={stripClass}
        style={{
          top: `${top}px`,
          left: 0,
          width: `${Math.max(0, left)}px`,
          height: `${height}px`,
        }}
        onClick={onDismissDim}
        aria-hidden
      />
      <div
        className={stripClass}
        style={{
          top: `${top}px`,
          left: `${left + width}px`,
          right: 0,
          height: `${height}px`,
        }}
        onClick={onDismissDim}
        aria-hidden
      />
      <div
        className={`${stripClass} bottom-0 left-0 right-0`}
        style={{ top: `${top + height}px` }}
        onClick={onDismissDim}
        aria-hidden
      />
      {/* Absorb stray taps on the highlighted region so users do not trigger UI underneath */}
      <div
        className="fixed z-[56] pointer-events-auto"
        style={{ top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` }}
        onClick={(e) => e.stopPropagation()}
        aria-hidden
      />
    </>
  )
}

export default function TutorialModal({ page }) {
  const { show, dismiss, dismissAll } = useTutorial(page)
  const content = useMemo(() => TUTORIAL_CONTENT[page] || DEFAULT_TUTORIAL_CONTENT, [page])
  const steps = content.steps || [{ text: content.title, highlight: null }]
  const [stepIndex, setStepIndex] = useState(0)
  const [cutoutRect, setCutoutRect] = useState(null)
  const [noCutoutTarget, setNoCutoutTarget] = useState(false)
  const dialogTitleId = `tutorial-title-${page || 'default'}-${stepIndex}`
  const dialogRef = useRef(null)
  const highlightedElRef = useRef(null)

  const step = steps[stepIndex]
  const isLastStep = stepIndex >= steps.length - 1
  const intro = step && isIntroStep(step)

  const headline = useMemo(() => {
    if (!step) return content.title
    if (intro) return step.title || content.title
    return step.heading || content.title
  }, [step, intro, content.title])

  const clearHighlight = useCallback(() => {
    if (highlightedElRef.current) {
      highlightedElRef.current.classList.remove(HIGHLIGHT_CLASS)
      highlightedElRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!show) return
    setStepIndex(0)
    return () => clearHighlight()
  }, [show, clearHighlight])

  useLayoutEffect(() => {
    if (!show || !step || intro) {
      clearHighlight()
      setCutoutRect(null)
      setNoCutoutTarget(false)
      return undefined
    }

    const highlightId = step.highlight
    if (!highlightId) {
      clearHighlight()
      setCutoutRect(null)
      setNoCutoutTarget(false)
      return undefined
    }

    const el = document.querySelector(`[data-tutorial="${highlightId}"]`)
    if (!el) {
      clearHighlight()
      setCutoutRect(null)
      setNoCutoutTarget(true)
      return undefined
    }

    setNoCutoutTarget(false)

    const PAD = 12
    const measure = () => {
      const r = el.getBoundingClientRect()
      setCutoutRect({
        top: Math.max(0, r.top - PAD),
        left: Math.max(0, r.left - PAD),
        width: r.width + 2 * PAD,
        height: r.height + 2 * PAD,
      })
    }

    clearHighlight()
    el.classList.add(HIGHLIGHT_CLASS)
    highlightedElRef.current = el

    const reduced =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({
      behavior: reduced ? 'instant' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })

    let raf = 0
    const scheduleMeasure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(measure)
      })
    }
    scheduleMeasure()

    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    const ro = new ResizeObserver(measure)
    ro.observe(el)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
      ro.disconnect()
      clearHighlight()
      setCutoutRect(null)
    }
  }, [show, stepIndex, step, intro, clearHighlight])

  const cardPlacement = useMemo(() => {
    if (!cutoutRect) return null
    return computeCardPlacement(cutoutRect)
  }, [cutoutRect])

  const spotlightWanted = Boolean(show && !intro && step?.highlight)
  const spotlightActive = Boolean(spotlightWanted && cutoutRect && cardPlacement)
  const spotlightWaiting = Boolean(spotlightWanted && !cutoutRect && !noCutoutTarget)
  const centerOverlay = Boolean(
    show && (intro || !step?.highlight || (spotlightWanted && noCutoutTarget))
  )

  useEffect(() => {
    if (!show) return undefined

    const handleEscape = (event) => {
      if (event.key === 'Escape') dismiss()
    }

    const handleTabTrap = (event) => {
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleEscape)
    window.addEventListener('keydown', handleTabTrap)
    return () => {
      window.removeEventListener('keydown', handleEscape)
      window.removeEventListener('keydown', handleTabTrap)
    }
  }, [show, dismiss])

  const handleNext = () => {
    if (isLastStep) dismiss()
    else setStepIndex((i) => i + 1)
  }

  const handleDismissAll = () => {
    dismissAll()
  }

  const cardBody = (
    <Card
      variant="flightcase"
      className="border-2 border-neon-cyan shadow-[0_0_25px_rgba(0,173,239,0.22)] p-5 sm:p-6 max-h-[min(72dvh,560px)] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <Lightbulb className="w-6 h-6 text-neon-cyan flex-shrink-0 mt-0.5" aria-hidden />
          {intro ? (
            <div className="min-w-0 text-left">
              {step.eyebrow ? (
                <p className="font-setlist text-xs uppercase tracking-widest text-neon-cyan font-bold mb-1">
                  {step.eyebrow}
                </p>
              ) : null}
              <h2
                id={dialogTitleId}
                className="font-poster text-2xl leading-tight text-white text-shadow-poster sm:text-3xl"
              >
                {headline}
              </h2>
            </div>
          ) : (
            <h2
              id={dialogTitleId}
              className="font-poster text-xl text-neon-cyan text-shadow-poster min-w-0"
            >
              {headline}
            </h2>
          )}
        </div>
        {steps.length > 1 && (
          <span className="font-specs text-xs text-gray-300 flex-shrink-0">
            {stepIndex + 1} / {steps.length}
          </span>
        )}
      </div>

      {intro ? (
        <ol className="space-y-2.5 mb-5 list-none pl-0">
          {step.blocks.map(({ icon: iconName, title: blockTitle, text: blockText }, i) => {
            const Icon = INTRO_ICONS[iconName] || Flag
            return (
              <li
                key={blockTitle}
                className="flex gap-3 p-3 rounded-lg bg-panel border-2 border-gray-700 sm:gap-4 sm:p-4"
              >
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-neon-cyan/15 border-2 border-neon-cyan/60 flex items-center justify-center">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-neon-cyan" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="font-setlist text-[11px] uppercase tracking-wider text-neon-cyan font-bold mb-0.5">
                    Step {i + 1}
                  </p>
                  <p className="font-setlist text-base sm:text-lg text-white leading-tight mb-0.5">
                    {blockTitle}
                  </p>
                  <p className="font-specs text-sm text-gray-300 leading-snug">{blockText}</p>
                </div>
              </li>
            )
          })}
        </ol>
      ) : (
        <p className="font-specs text-white text-base leading-relaxed mb-6 pl-9">{step?.text}</p>
      )}

      <Button variant="sticker" onClick={handleNext} className="w-full min-h-12 text-base" autoFocus>
        {isLastStep ? 'Got it!' : 'Next'}
      </Button>

      {step?.footerHint ? (
        <p className="font-specs text-xs text-center text-gray-300 mt-3">
          You can always replay tips from the menu.
        </p>
      ) : null}

      <button
        type="button"
        onClick={handleDismissAll}
        className="mt-3 mx-auto block min-h-11 px-2 font-setlist text-xs uppercase tracking-wider text-neon-pink underline hover:text-white transition-colors"
      >
        Skip all tips
      </button>
    </Card>
  )

  return (
    <AnimatePresence>
      {show && spotlightActive && cutoutRect && cardPlacement && (
        <motion.div
          key={`spotlight-${page}-${stepIndex}`}
          className="fixed inset-0 z-50 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <SpotlightShroud rect={cutoutRect} onDismissDim={dismiss} />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="fixed z-[70] pointer-events-auto p-3 sm:p-4"
            style={{
              top: cardPlacement.top,
              left: cardPlacement.left,
              width: cardPlacement.width,
              maxHeight: cardPlacement.maxHeight,
            }}
            ref={dialogRef}
          >
            {cardBody}
          </motion.div>
        </motion.div>
      )}

      {show && spotlightWaiting && (
        <div
          key={`spotlight-wait-${page}-${stepIndex}`}
          className="fixed inset-0 z-50 cursor-wait pointer-events-auto"
          style={{ opacity: 0 }}
          aria-busy="true"
          aria-hidden
        />
      )}

      {show && centerOverlay && (
        <motion.div
          key={`center-${page}-${stepIndex}-${intro ? 'intro' : 'full'}`}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto max-h-[100dvh] pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
          aria-label="Close tips"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            className="w-full max-w-xl my-auto"
            ref={dialogRef}
            onClick={(e) => e.stopPropagation()}
          >
            {cardBody}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
